import { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileSpreadsheet, Download, Search, Calendar, Filter, Users, Stethoscope, TrendingUp, BarChart3, Activity, Clock, UserPlus, LogOut, Loader2 } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#14b8a6', '#ec4899', '#6366f1',
  '#84cc16', '#a855f7'
];

const HeatmapCell = ({ value, maxVal }) => {
  const intensity = maxVal > 0 ? value / maxVal : 0;
  const bg = value === 0
    ? 'bg-gray-100 dark:bg-gray-800'
    : intensity < 0.25
      ? 'bg-blue-100 dark:bg-blue-900/40'
      : intensity < 0.5
        ? 'bg-blue-300 dark:bg-blue-700/60'
        : intensity < 0.75
          ? 'bg-blue-500 dark:bg-blue-600 text-white'
          : 'bg-blue-700 dark:bg-blue-500 text-white';
  return (
    <td className={`p-1 text-center text-xs font-mono ${bg} border`} title={`${value} pacientes`}>
      {value}
    </td>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, color = 'blue' }) => {
  const colorMap = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600',
    cyan: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600',
  };
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-lg ${colorMap[color]}`}><Icon className="h-5 w-5" /></div>
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
            {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CustomTooltipPie = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-background border rounded-lg shadow-lg p-2 text-sm">
        <p className="font-medium">{payload[0].name}</p>
        <p className="text-muted-foreground">{payload[0].value} pacientes ({((payload[0].value / payload[0].payload.total) * 100).toFixed(1)}%)</p>
      </div>
    );
  }
  return null;
};

const Relatorios = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('dia-a-dia');

  // ---- Dia a Dia State ----
  const [escopo, setEscopo] = useState('todos');
  const [equipes, setEquipes] = useState([]);
  const [equipeId, setEquipeId] = useState('');
  const [internacoes, setInternacoes] = useState([]);
  const [internacaoId, setInternacaoId] = useState('');
  const [tipoData, setTipoData] = useState('periodo');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [dataEspecifica, setDataEspecifica] = useState('');
  const [registros, setRegistros] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalPacientes, setTotalPacientes] = useState(0);
  const [totalDias, setTotalDias] = useState(0);
  const [searchPaciente, setSearchPaciente] = useState('');
  const [chartView, setChartView] = useState('equipe');

  // ---- KPI State ----
  const [kpiInicio, setKpiInicio] = useState('');
  const [kpiFim, setKpiFim] = useState('');
  const [kpiData, setKpiData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);

  // ---- Especialidades Report State ----
  const [espData, setEspData] = useState(null);
  const [espLoading, setEspLoading] = useState(false);

  const fetchEquipes = useCallback(async () => {
    try {
      const res = await api.get('/equipes');
      setEquipes(res.data?.equipes?.filter(e => e.id !== 'sem_equipe') || []);
    } catch (err) { console.error(err); }
  }, [api]);

  const fetchInternacoes = useCallback(async () => {
    try {
      const res = await api.get('/internacoes?limit=500');
      setInternacoes(res.data || []);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => {
    fetchEquipes();
    fetchInternacoes();
    const hoje = new Date();
    const semanaAtras = new Date(hoje);
    semanaAtras.setDate(hoje.getDate() - 7);
    const mesAtras = new Date(hoje);
    mesAtras.setDate(hoje.getDate() - 30);
    setDataFim(hoje.toISOString().split('T')[0]);
    setDataInicio(semanaAtras.toISOString().split('T')[0]);
    setKpiFim(hoje.toISOString().split('T')[0]);
    setKpiInicio(mesAtras.toISOString().split('T')[0]);
  }, [fetchEquipes, fetchInternacoes]);

  // ---- Especialidades Report ----
  const fetchEspRelatorio = useCallback(async () => {
    setEspLoading(true);
    try {
      const res = await api.get('/fisioterapia/relatorio');
      setEspData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar relatório de especialidades');
    } finally { setEspLoading(false); }
  }, [api]);

  useEffect(() => {
    if (activeTab === 'especialidades') fetchEspRelatorio();
  }, [activeTab, fetchEspRelatorio]);

  // ---- Dia a Dia ----
  const buildDiaParams = () => {
    const params = new URLSearchParams();
    params.append('escopo', escopo);
    if (escopo === 'equipe' && equipeId) params.append('equipe_id', equipeId);
    if (escopo === 'paciente' && internacaoId) params.append('internacao_id', internacaoId);
    if (tipoData === 'especifica' && dataEspecifica) {
      params.append('data_especifica', dataEspecifica);
    } else if (dataInicio && dataFim) {
      params.append('data_inicio', dataInicio);
      params.append('data_fim', dataFim);
    }
    return params.toString();
  };

  const handleGerar = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/relatorios/dia-a-dia?${buildDiaParams()}`);
      setRegistros(res.data.registros || []);
      setChartData(res.data.chart_data || null);
      setTotalPacientes(res.data.total_pacientes || 0);
      setTotalDias(res.data.total_dias || 0);
      toast.success(`Relatório gerado: ${res.data.registros?.length || 0} registros`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const handleExportarDia = async () => {
    try {
      const res = await api.get(`/relatorios/dia-a-dia/exportar?${buildDiaParams()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const p = tipoData === 'especifica' ? dataEspecifica : `${dataInicio}_a_${dataFim}`;
      link.setAttribute('download', `relatorio_dia_a_dia_${p}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório exportado!');
    } catch (err) {
      toast.error('Erro ao exportar');
    }
  };

  // ---- KPIs ----
  const handleGerarKpis = async () => {
    setKpiLoading(true);
    try {
      const params = new URLSearchParams();
      if (kpiInicio) params.append('data_inicio', kpiInicio);
      if (kpiFim) params.append('data_fim', kpiFim);
      const res = await api.get(`/relatorios/kpis?${params.toString()}`);
      setKpiData(res.data);
      toast.success('KPIs gerados com sucesso');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar KPIs');
    } finally {
      setKpiLoading(false);
    }
  };

  const handleExportarKpis = async () => {
    try {
      const params = new URLSearchParams();
      if (kpiInicio) params.append('data_inicio', kpiInicio);
      if (kpiFim) params.append('data_fim', kpiFim);
      const res = await api.get(`/relatorios/kpis/exportar?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kpis_${kpiInicio}_a_${kpiFim}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('KPIs exportados!');
    } catch (err) {
      toast.error('Erro ao exportar');
    }
  };

  const filteredRegistros = searchPaciente
    ? registros.filter(r => r.paciente_nome.toLowerCase().includes(searchPaciente.toLowerCase()))
    : registros;

  const groupedByDate = useMemo(() => {
    const g = {};
    filteredRegistros.forEach(r => {
      if (!g[r.data]) g[r.data] = [];
      g[r.data].push(r);
    });
    return g;
  }, [filteredRegistros]);

  const canGenerate = () => {
    if (escopo === 'equipe' && !equipeId) return false;
    if (escopo === 'paciente' && !internacaoId) return false;
    if (tipoData === 'especifica' && !dataEspecifica) return false;
    if (tipoData === 'periodo' && (!dataInicio || !dataFim)) return false;
    return true;
  };

  const lineData = chartView === 'equipe' ? chartData?.line_equipe : chartData?.line_medico;
  const lineNames = chartView === 'equipe' ? chartData?.equipe_names : chartData?.medico_names;
  const heatmapData = chartView === 'equipe' ? chartData?.heatmap_equipe : chartData?.heatmap_medico;
  const heatmapMax = useMemo(() => {
    if (!heatmapData) return 1;
    let max = 0;
    heatmapData.forEach(row => row.data.forEach(c => { if (c.value > max) max = c.value; }));
    return max || 1;
  }, [heatmapData]);

  const formatDateShort = (d) => { if (!d) return ''; const p = d.split('-'); return `${p[2]}/${p[1]}`; };

  // Prepare KPI pie data with totals
  const addTotal = (list) => {
    const total = list.reduce((s, i) => s + i.valor, 0);
    return list.map(i => ({ ...i, total }));
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="relatorios-title">Relatórios</h1>
          <p className="text-muted-foreground text-sm">Análise de dados e indicadores de desempenho</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="dia-a-dia" data-testid="tab-dia-a-dia">Dia a Dia</TabsTrigger>
            <TabsTrigger value="kpis" data-testid="tab-kpis">KPIs</TabsTrigger>
            <TabsTrigger value="especialidades" data-testid="tab-especialidades">Especialidades</TabsTrigger>
          </TabsList>

          {/* =============== DIA A DIA TAB =============== */}
          <TabsContent value="dia-a-dia" className="space-y-6">
            {/* Filters */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><Filter className="h-4 w-4" /> Filtros</CardTitle>
                  {registros.length > 0 && (
                    <Button onClick={handleExportarDia} variant="outline" size="sm" className="gap-1" data-testid="btn-exportar-relatorio">
                      <Download className="h-3.5 w-3.5" /> Exportar Excel
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Escopo</Label>
                    <Select value={escopo} onValueChange={setEscopo}>
                      <SelectTrigger data-testid="select-escopo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos os pacientes</SelectItem>
                        <SelectItem value="equipe">Por equipe</SelectItem>
                        <SelectItem value="paciente">Paciente especifico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {escopo === 'equipe' && (
                    <div className="space-y-2">
                      <Label>Equipe</Label>
                      <Select value={equipeId} onValueChange={setEquipeId}>
                        <SelectTrigger data-testid="select-equipe-filtro"><SelectValue placeholder="Selecione a equipe" /></SelectTrigger>
                        <SelectContent>{equipes.map(eq => (<SelectItem key={eq.id} value={eq.id}>{eq.nome}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {escopo === 'paciente' && (
                    <div className="space-y-2">
                      <Label>Paciente</Label>
                      <Select value={internacaoId} onValueChange={setInternacaoId}>
                        <SelectTrigger data-testid="select-paciente-filtro"><SelectValue placeholder="Selecione o paciente" /></SelectTrigger>
                        <SelectContent>{internacoes.map(i => (<SelectItem key={i.id} value={i.id}>{i.paciente_nome || 'Sem nome'} - {i.data_internacao?.slice(0,10)}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Tipo de data</Label>
                    <Select value={tipoData} onValueChange={setTipoData}>
                      <SelectTrigger data-testid="select-tipo-data"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="periodo">Periodo (inicio/fim)</SelectItem>
                        <SelectItem value="especifica">Data especifica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {tipoData === 'periodo' ? (
                    <>
                      <div className="space-y-2"><Label>Data inicio</Label><Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} data-testid="input-data-inicio" /></div>
                      <div className="space-y-2"><Label>Data fim</Label><Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} data-testid="input-data-fim" /></div>
                    </>
                  ) : (
                    <div className="space-y-2"><Label>Data</Label><Input type="date" value={dataEspecifica} onChange={e => setDataEspecifica(e.target.value)} data-testid="input-data-especifica" /></div>
                  )}
                </div>
                <div className="flex justify-end mt-4">
                  <Button onClick={handleGerar} disabled={!canGenerate() || loading} className="gap-2" data-testid="btn-gerar-relatorio">
                    <Search className="h-4 w-4" /> {loading ? 'Gerando...' : 'Gerar relatório'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            {registros.length > 0 && (
              <div className="grid grid-cols-3 gap-4">
                <KpiCard icon={FileSpreadsheet} label="Registros" value={filteredRegistros.length} color="blue" />
                <KpiCard icon={Users} label="Pacientes" value={totalPacientes} color="green" />
                <KpiCard icon={Calendar} label="Dias" value={totalDias} color="amber" />
              </div>
            )}

            {/* Charts */}
            {chartData && registros.length > 0 && totalDias > 1 && (
              <>
                <div className="flex gap-2">
                  <Button variant={chartView === 'equipe' ? 'default' : 'outline'} size="sm" onClick={() => setChartView('equipe')} className="gap-1" data-testid="btn-chart-equipe">
                    <Users className="h-3.5 w-3.5" /> Por equipe
                  </Button>
                  <Button variant={chartView === 'medico' ? 'default' : 'outline'} size="sm" onClick={() => setChartView('medico')} className="gap-1" data-testid="btn-chart-medico">
                    <Stethoscope className="h-3.5 w-3.5" /> Por médico
                  </Button>
                </div>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Evolução de pacientes por {chartView === 'equipe' ? 'equipe' : 'médico'}</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={lineData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="data" tickFormatter={formatDateShort} tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={v => { const p = v.split('-'); return `${p[2]}/${p[1]}/${p[0]}`; }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {lineNames?.map((name, i) => (
                          <Line key={name} type="monotone" dataKey={name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Carga por {chartView === 'equipe' ? 'equipe' : 'médico'} (heatmap)</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="heatmap-table">
                        <thead>
                          <tr>
                            <th className="text-left p-2 font-medium min-w-[140px] sticky left-0 bg-background z-10">{chartView === 'equipe' ? 'Equipe' : 'Médico(a)'}</th>
                            {chartData?.dias?.map(d => (<th key={d} className="p-1 text-center text-xs font-mono font-normal whitespace-nowrap">{formatDateShort(d)}</th>))}
                            <th className="p-2 text-center font-medium text-xs">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {heatmapData?.map(row => {
                            const rowTotal = row.data.reduce((s, c) => s + c.value, 0);
                            return (
                              <tr key={row.name}>
                                <td className="p-2 font-medium text-xs sticky left-0 bg-background z-10 border-r">{row.name}</td>
                                {row.data.map((cell, ci) => (<HeatmapCell key={ci} value={cell.value} maxVal={heatmapMax} />))}
                                <td className="p-1 text-center text-xs font-bold border-l">{rowTotal}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                      <span>Intensidade:</span>
                      <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-800 border"></div><span>0</span></div>
                      <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-blue-100 dark:bg-blue-900/40 border"></div><span>Baixa</span></div>
                      <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-blue-300 dark:bg-blue-700/60 border"></div><span>Média</span></div>
                      <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-blue-500 dark:bg-blue-600 border"></div><span>Alta</span></div>
                      <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-blue-700 dark:bg-blue-500 border"></div><span>Muito alta</span></div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Table */}
            {registros.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Detalhamento</CardTitle>
                    <div className="w-64"><Input placeholder="Filtrar por paciente..." value={searchPaciente} onChange={e => setSearchPaciente(e.target.value)} data-testid="input-search-relatorio" /></div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-md overflow-auto max-h-[500px]">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left p-3 font-medium">Data</th>
                          <th className="text-left p-3 font-medium">Paciente</th>
                          <th className="text-left p-3 font-medium"><div className="flex items-center gap-1"><Users className="h-3 w-3" /> Equipe</div></th>
                          <th className="text-left p-3 font-medium"><div className="flex items-center gap-1"><Stethoscope className="h-3 w-3" /> Médico(a)</div></th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(groupedByDate).map(([date, items]) => (
                          items.map((r, idx) => (
                            <tr key={`${date}-${r.internacao_id}-${idx}`} className={`border-t ${idx === 0 ? 'bg-muted/20' : ''}`} data-testid={`relatorio-row-${date}-${idx}`}>
                              <td className="p-3 font-mono text-xs">{idx === 0 ? <Badge variant="outline" className="font-mono">{new Date(date + 'T12:00:00').toLocaleDateString('pt-BR')}</Badge> : ''}</td>
                              <td className="p-3 font-medium">{r.paciente_nome}</td>
                              <td className="p-3"><Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400">{r.equipe_nome}</Badge></td>
                              <td className="p-3"><Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400">{r.medico_nome}</Badge></td>
                            </tr>
                          ))
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
            {registros.length === 0 && !loading && (
              <Card><CardContent className="py-12 text-center text-muted-foreground"><FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-lg font-medium">Nenhum relatório gerado</p><p className="text-sm">Selecione os filtros e clique em "Gerar relatório"</p></CardContent></Card>
            )}
          </TabsContent>

          {/* =============== KPIs TAB =============== */}
          <TabsContent value="kpis" className="space-y-6">
            {/* KPI Filters */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><Filter className="h-4 w-4" /> Periodo de análise</CardTitle>
                  {kpiData && (
                    <Button onClick={handleExportarKpis} variant="outline" size="sm" className="gap-1" data-testid="btn-exportar-kpis">
                      <Download className="h-3.5 w-3.5" /> Exportar Excel
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-4">
                  <div className="space-y-2">
                    <Label>Data inicio</Label>
                    <Input type="date" value={kpiInicio} onChange={e => setKpiInicio(e.target.value)} data-testid="input-kpi-inicio" />
                  </div>
                  <div className="space-y-2">
                    <Label>Data fim</Label>
                    <Input type="date" value={kpiFim} onChange={e => setKpiFim(e.target.value)} data-testid="input-kpi-fim" />
                  </div>
                  <Button onClick={handleGerarKpis} disabled={kpiLoading} className="gap-2" data-testid="btn-gerar-kpis">
                    <BarChart3 className="h-4 w-4" /> {kpiLoading ? 'Gerando...' : 'Gerar KPIs'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* KPI Cards */}
            {kpiData && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <KpiCard icon={Users} label="Pacientes ativos" value={kpiData.cards.total_ativos} color="blue" />
                  <KpiCard icon={Activity} label="Taxa de ocupação" value={`${kpiData.cards.taxa_ocupacao}%`} sub={`${kpiData.cards.total_ativos}/${kpiData.cards.capacidade_total}`} color={kpiData.cards.taxa_ocupacao > 80 ? 'red' : kpiData.cards.taxa_ocupacao > 60 ? 'amber' : 'green'} />
                  <KpiCard icon={Clock} label="Tempo médio (dias)" value={kpiData.cards.tempo_medio_internacao} color="purple" />
                  <KpiCard icon={UserPlus} label="Novas admissões" value={kpiData.cards.novas_admissoes_periodo} sub="no periodo" color="cyan" />
                  <KpiCard icon={LogOut} label="Altas" value={kpiData.cards.altas_periodo} sub="no periodo" color="green" />
                  <KpiCard icon={BarChart3} label="Capacidade total" value={kpiData.cards.capacidade_total} color="amber" />
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Por Origem */}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Distribuição por origem</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie data={addTotal(kpiData.distribuicoes.por_origem)} dataKey="valor" nameKey="nome" cx="50%" cy="50%" outerRadius={90} label={({ nome, percent }) => `${nome} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                            {kpiData.distribuicoes.por_origem.map((entry, i) => (<Cell key={entry.nome || i} fill={COLORS[i % COLORS.length]} />))}
                          </Pie>
                          <Tooltip content={<CustomTooltipPie />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Por Status */}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Distribuição por status</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={kpiData.distribuicoes.por_status} layout="vertical" margin={{ left: 100 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="nome" tick={{ fontSize: 11 }} width={95} />
                          <Tooltip />
                          <Bar dataKey="valor" name="Pacientes" radius={[0, 4, 4, 0]}>
                            {kpiData.distribuicoes.por_status.map((entry, i) => (<Cell key={entry.nome || i} fill={COLORS[i % COLORS.length]} />))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Por Equipe */}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Pacientes por equipe</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={kpiData.distribuicoes.por_equipe}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="nome" tick={{ fontSize: 10, angle: -25 }} height={50} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="valor" name="Pacientes" radius={[4, 4, 0, 0]}>
                            {kpiData.distribuicoes.por_equipe.map((entry, i) => (<Cell key={entry.nome || i} fill={COLORS[i % COLORS.length]} />))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Por Médico */}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Pacientes por médico</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={kpiData.distribuicoes.por_medico}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="nome" tick={{ fontSize: 10 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="valor" name="Pacientes" radius={[4, 4, 0, 0]}>
                            {kpiData.distribuicoes.por_medico.map((entry, i) => (<Cell key={entry.nome || i} fill={COLORS[i % COLORS.length]} />))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Tempo de Internação */}
                  <Card className="lg:col-span-2">
                    <CardHeader className="pb-2"><CardTitle className="text-base">Distribuição por tempo de internação</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={kpiData.distribuicoes.por_tempo_internacao}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="nome" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="valor" name="Pacientes" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}

            {!kpiData && !kpiLoading && (
              <Card><CardContent className="py-12 text-center text-muted-foreground"><BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-lg font-medium">Nenhum KPI gerado</p><p className="text-sm">Selecione o periodo e clique em "Gerar KPIs"</p></CardContent></Card>
            )}
          </TabsContent>

          {/* =============== ESPECIALIDADES TAB =============== */}
          <TabsContent value="especialidades" className="space-y-6">
            {espLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : espData ? (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {espData.por_especialidade.map((esp, idx) => {
                    const colors = ['green', 'purple', 'amber', 'blue'];
                    return (
                      <KpiCard key={esp.key} icon={Activity}
                        label={esp.label}
                        value={esp.pacientes_ativos}
                        sub={`${esp.total_evolucoes} sessões registradas`}
                        color={colors[idx] || 'blue'}
                      />
                    );
                  })}
                </div>

                {/* Total summary */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <KpiCard icon={Users} label="Total de Pacientes com Especialidade" value={espData.resumo.total_pacientes_com_especialidade} color="cyan" />
                  <KpiCard icon={FileSpreadsheet} label="Total de Evoluções Registradas" value={espData.resumo.total_evolucoes_registradas} color="purple" />
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Pie chart */}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Distribuição por Especialidade</CardTitle></CardHeader>
                    <CardContent>
                      {espData.chart_data.some(d => d.value > 0) ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie data={espData.chart_data.filter(d => d.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: ${value}`}>
                              {espData.chart_data.filter(d => d.value > 0).map((_, i) => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-center text-sm text-muted-foreground py-8">Nenhum paciente com especialidade ativa</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Monthly evolution chart */}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Evolução Mensal de Sessões</CardTitle></CardHeader>
                    <CardContent>
                      {espData.evolucao_mensal.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={espData.evolucao_mensal}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="fisio" name="Fisioterapia" fill="#10b981" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="fono" name="Fonoaudiologia" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="nutri" name="Nutrição" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="psicologo" name="Psicologia" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="clinico" name="Clínico" fill="#f43f5e" radius={[2, 2, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-center text-sm text-muted-foreground py-8">Nenhuma sessão registrada nos últimos 6 meses</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Export */}
                <div className="flex justify-end">
                  <Button variant="outline" className="gap-2" data-testid="btn-export-especialidades"
                    onClick={async () => {
                      try {
                        const res = await api.get('/fisioterapia/relatorio/export?formato=csv', { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement('a'); a.href = url; a.download = `relatorio_especialidades.csv`; a.click();
                        window.URL.revokeObjectURL(url);
                        toast.success('CSV exportado');
                      } catch { toast.error('Erro ao exportar'); }
                    }}>
                    <Download className="h-4 w-4" /> Exportar CSV
                  </Button>
                </div>
              </>
            ) : (
              <Card><CardContent className="py-12 text-center text-muted-foreground">
                <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium">Carregando relatório...</p>
              </CardContent></Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Relatorios;
